import React, {
    useEffect
} from 'react';
import PropTypes from 'prop-types';

import Panel from '../../../components/Panel.jsx';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import MetricTableCell from '../../../components/table/MetricTableCell.jsx';
import MetricTimePeriodHeaderTitle from '../../../components/table/MetricTimePeriodHeaderTitle.jsx';

import {
    useIsExporting,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useMetrics from '../../../hooks/useMetrics.js';

import {
    useComputedCurationConfigValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import useViewListeningQuery from '../hooks/useViewListeningQuery.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

import DeleteListeningQueryButton from './DeleteListeningQueryButton.jsx';

import {
    RIQInfoPopover,
    useRIQTooltip
} from '../../../components/RIQPopover.jsx';

import {
    LoadingStatus,
    useSetInstanceLoadingStatus
} from '../../../hooks/loadingStatusHooks.js';

import useCDNUrl from '../../../hooks/useCDNUrl.js';

const {
    title,
    description,
    subtitle = description,
    topicMetaInfo,
    topicOrThemeUnavailable,
    topicOrThemeUnavailableInfoBubble
} = listeningDefinitions['quid-topics'];

function QuidTopicsTable({metricRequests, instanceId}) {
    const emptyListeningUrl = useCDNUrl('/images/listening-empty2.svg');
    const isExporting = useIsExporting();
    const statusUpdater = useSetInstanceLoadingStatus(instanceId);

    const {data: metricResults, isLoading: isMetricsLoading} = useMetrics(metricRequests);

    // NOTE we do this monkey business so that exports wait until the metrics
    // have finished loading before capture, thereby preventing accidental
    // capture during loading states
    useEffect(() => {
        statusUpdater(s => LoadingStatus[(isMetricsLoading ? 'LOADING' : 'COMPLETE')]);
    }, [isMetricsLoading, statusUpdater]);

    if (isMetricsLoading) {
        return (
            <div className='tlw-grid tlw-h-[100px] tlw-place-items-center'>
                <div className='tlw-text-[36px] tlw-text-gray-500'>
                    <span className='fa fa-circle-o-notch fa-spin'></span>
                </div>
            </div>
        );
    }

    const results = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(_.omit(metricResults, ['countByDay', 'netSentimentByDay']));

    const listeningQueryIdx = _.indexOf(metricResults.countByDay.dimensions, 'listeningQueryId');
    const countByDayResults = metricResults.countByDay;
    const netSentimentByDayResults = metricResults.netSentimentByDay;

    const countByDayListeningQueryId = _(countByDayResults.data)
        .map((row) => {
            return [row[listeningQueryIdx], _.last(row)];
        })
        .groupBy(0)
        .value();

    const netSentimentByDayListeningQueryId = _(netSentimentByDayResults.data)
        .map((row) => {
            return [row[listeningQueryIdx], _.last(row)];
        })
        .groupBy(0)
        .value();

    _.each(results, result => {
        const listeningQueryId = result.topicName[0];
        result.id = listeningQueryId;
        result.countByDay = countByDayListeningQueryId[listeningQueryId];
        result.netSentimentByDay = netSentimentByDayListeningQueryId[listeningQueryId];
    });

    const formattersByRequestName = metricResults ? _.mapValues(metricRequests, (request, requestName) => {
        const metricResult = metricResults[requestName];
        return {
            abbreviated: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
            expanded: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResult, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded)
        };
    }) : {};

    let tableBody;
    if (_.isEmpty(results)) {
        tableBody = (
            <tr>
                <td
                    className='tlw-p-10 tlw-text-center'
                    colSpan='7'>
                    <img
                        className='tlw-m-auto tlw-max-h-[100px]'
                        alt='error'
                        src={emptyListeningUrl}
                    />
                    <h3 className='tlw-text-lg'>This landscape doesn&rsquo;t have any connected Quid Monitor Topics.</h3>
                    <p className='tlw-text-sm'>Connect Topics to easily access and monitor your social listening data.</p>
                </td>
            </tr>
        );
    } else {
        tableBody = _.map(results, listeningQuery => {
            return (
                <TableRow
                    key={listeningQuery.id}
                    listeningQueryData={listeningQuery}
                    formattersByRequestName={formattersByRequestName}
                    metricRequests={metricRequests}
                />
            );
        });
    }

    return (
        <div className='tlw-overflow-x-auto max-sm_tlw-w-full max-sm_tlw-overflow-y-hidden'>
            <table className='tlw-w-full'>
                <thead>
                    <tr className='tlw-bg-gray-150'>
                        <MetricTableHeader
                            title={`Topic Name`}
                        />
                        <MetricTableHeader
                            title={`Theme`}
                            className='max-sm_!tlw-min-w-96 md_!tlw-w-96'
                        />
                        <MetricTableHeader
                            title='Share of Voice'
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[120px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.shareOfVoice}
                        />
                        <MetricTableHeader
                            title='Posts'
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.count}
                        />
                        <MetricTableHeader
                            title='Net Sentiment'
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.netSentiment}
                        />
                        <MetricTableHeader
                            title={<MetricTimePeriodHeaderTitle
                                metric='Posts'
                                timePeriod='Last 24 Hours'/>}
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[120px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.countLast24Hours}
                        />
                        <MetricTableHeader
                            title={<MetricTimePeriodHeaderTitle
                                metric='Net Sentiment'
                                timePeriod='Last 24 Hours'/>}
                            titleFormatter={null}
                            alignment={MetricTableHeader.Alignment.RIGHT}
                            className='tlw-min-w-[120px] max-sm_tlw-whitespace-nowrap'
                            description={listeningDefinitions.metrics.netSentimentLast24Hours}
                        />
                        {!isExporting &&
                                <th
                                    className='tlw-w-30'
                                />
                        }
                    </tr>
                </thead>
                <tbody>{tableBody}</tbody>
            </table>
        </div>);
}

function shareOfVoiceFormatter(value, abbreviated = false) {
    const formatter = new Intl.NumberFormat('en-us', {
        maximumFractionDigits: (abbreviated ? 0 : 2),
        style: 'percent'
    });
    return formatter.format(value);
}

function TableRow({listeningQueryData, formattersByRequestName, metricRequests}) {

    const isExporting = useIsExporting();
    const viewListeningQuery = useViewListeningQuery();
    const {setTriggerRef: deleteTriggerRef, tooltipNode: deleteTooltipNode} = useRIQTooltip({
        text: 'Delete Topic',
        placement: 'top',
        delayShow: 200
    });

    const themeNamesDisplay = _.tail(listeningQueryData.themeNamesDisplay);
    const {setTriggerRef: themeTriggerRef, tooltipNode: themeTooltipNode} = useRIQTooltip({
        text: themeNamesDisplay,
        placement: 'top',
        delayShow: 200
    });

    const listeningQueryId = _.head(listeningQueryData.topicName);
    const topicName = _.tail(listeningQueryData.topicName);
    const topicDescription = _.tail(listeningQueryData.topicDescription);
    const topicTimeInterval = _.tail(listeningQueryData.topicTimeInterval);
    const topicLanguageFilter = _.tail(listeningQueryData.topicLanguageFilter);
    const topicSentimentFocus = _.tail(listeningQueryData.topicSentimentFocus);
    const listeningQuery = {
        id: listeningQueryId,
        name: topicName
    };
    const searchable = (topicName[0] !== null && themeNamesDisplay[0] !== null);

    const metaInfo = topicMetaInfo(topicDescription, themeNamesDisplay[0], topicTimeInterval[0], topicLanguageFilter[0], topicSentimentFocus[0]);
    const TopicValue = searchable ? (
        <span className='tlw-flex tlw-flex-row'>
            <span className='tlw-max-w-[30ch] tlw-truncate'>{topicName}</span>
            <RIQInfoPopover
                title={topicName[0]}
                description={metaInfo}
            />
        </span>) : (
        <span className='tlw-flex tlw-flex-row'>
            <span className='font-italic tlw-max-w-[30ch] tlw-truncate tlw-opacity-50'>{topicOrThemeUnavailable}</span>
            <RIQInfoPopover
                description={topicOrThemeUnavailableInfoBubble}
            />
        </span>);

    const keys = [
        'shareOfVoice',
        'count',
        'netSentiment',
        'countLast24Hours',
        'netSentimentLast24Hours'
    ];

    const rowClassNames = 'tlw-w-full tlw-truncate tlw-text-right max-sm_tlw-whitespace-nowrap';
    function sentimentRowClassNames(sentiment) {
        return `tlw-w-full tlw-truncate tlw-text-right ${sentiment < 0 ? 'tlw-text-red-400' : 'tlw-text-green-400'} max-sm_tlw-whitespace-nowrap`;
    }

    // shareOfVoice uses the formatter defined above
    formattersByRequestName.shareOfVoice = {
        abbreviated: function (val) {
            return shareOfVoiceFormatter(val, true);
        },
        expanded: shareOfVoiceFormatter
    };

    return (
        <tr
            key={listeningQueryId}
            className={`tlw-h-[47px] tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50 ${searchable ? 'tlw-cursor-pointer' : 'tlw-cursor-not-allowed'}`}
            onClick={() => searchable && viewListeningQuery(listeningQueryData.topicName[0], RivalIQ.enums.listeningQueryTypes.QuidTopic.id)}
        >
            <td className='tlw-p-4'>{TopicValue}</td>

            <td
                className='tlw-p-4'
            >
                <span
                    className='tlw-block tlw-max-w-96 tlw-truncate'
                    ref={themeTriggerRef}
                >
                    {searchable ? themeNamesDisplay : null}
                </span>
                {themeNamesDisplay && themeTooltipNode}
            </td>

            {
                _.map(keys, function (key) {
                    const metricValue = listeningQueryData?.[key]?.[1] || [];
                    const abbreviatedValue = _.isNumber(metricValue) ? formattersByRequestName[key].abbreviated(metricValue) : '--';
                    const expandedValue = _.isNumber(metricValue) ? formattersByRequestName[key].expanded(metricValue) : '--';
                    const sparklineData = listeningQueryData?.[`${key}ByDay`];
                    return (
                        <MetricTableCell
                            key={key}
                            className={key.includes('netSentiment') ? sentimentRowClassNames(metricValue) : rowClassNames}
                            abbreviatedValue={abbreviatedValue}
                            metricName={RivalIQ.metrics.new.MetricRegistry.getName(metricRequests[key], true)}
                            expandedValue={expandedValue}
                            alignment={MetricTableCell.Alignment.RIGHT}
                            sparklineData={sparklineData}
                        />
                    );
                })
            }

            {!isExporting &&
                <td
                    className='tlw-w-[84px] tlw-text-right'
                >
                    <div
                        className='tlw-flex tlw-h-full tlw-flex-row tlw-justify-end'
                    >
                        <DeleteListeningQueryButton
                            listeningQuery={listeningQuery}
                            ref={deleteTriggerRef}
                        />
                        {deleteTooltipNode}
                    </div>
                </td>
            }
        </tr>
    );
}

function useQuidTopicMetricRequests() {
    const landscapeId = useCurationConfigValue('landscapeId');
    const timezone = useCurationConfigValue('timezone');
    const dateRanges = useComputedCurationConfigValue('dateRanges');

    const timePeriodMainPeriodStart = dateRanges.main.start;
    const timePeriodMainPeriodEnd = dateRanges.main.end;

    const baseDate = moment.utc().startOf('hour').subtract(1, 'hour');
    const mainPeriodStartDate = baseDate.clone().subtract(24, 'hours').toISOString();
    const mainPeriodEndDate = baseDate.toISOString();

    const listeningFilters = {
        landscapeId: landscapeId,
        listeningQueryTypes: [
            RivalIQ.enums.listeningQueryTypes.QuidTopic.id
        ]
    };

    const metricRequests = {
        topicName: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicName',
            filters: {
                listening: listeningFilters
            }
        },
        topicDescription: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicDescription',
            filters: {
                listening: listeningFilters
            }
        },
        topicTimeInterval: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicTimeInterval',
            filters: {
                listening: listeningFilters,
                timezone: {
                    name: timezone
                }
            }
        },
        themeNamesDisplay: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'themeNamesDisplay',
            filters: {
                listening: listeningFilters
            }
        },
        topicLanguageFilter: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicLanguageFilter',
            filters: {
                listening: listeningFilters
            }
        },
        topicSentimentFocus: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicSentimentFocus',
            filters: {
                listening: listeningFilters
            }
        },
        shareOfVoice: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: {
                field: 'count',
                transform: 'percentOfTotal'
            },
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        },
        count: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'count',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        },
        countByDay: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            measure: 'count',
            dimensions: ['listeningQueryId', 'day'],
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        },
        netSentiment: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'netSentiment',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        },
        netSentimentByDay: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId', 'day'],
            measure: 'netSentiment',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: timePeriodMainPeriodStart,
                    endDate: timePeriodMainPeriodEnd
                }
            }
        },
        countLast24Hours: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'count',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: mainPeriodStartDate,
                    endDate: mainPeriodEndDate
                }
            }
        },
        netSentimentLast24Hours: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: ['listeningQueryId'],
            measure: 'netSentiment',
            filters: {
                listening: listeningFilters,
                timePeriod: {
                    timezone: timezone,
                    startDate: mainPeriodStartDate,
                    endDate: mainPeriodEndDate
                }
            }
        }
    };

    return metricRequests;
}

QuidTopicListWidget.propTypes = {
    instanceId: PropTypes.string.isRequired
};

export default function QuidTopicListWidget({instanceId}) {

    const metricRequests = useQuidTopicMetricRequests();
    const topicMetrics = ['topicName', 'themeNamesDisplay', 'shareOfVoice', 'count', 'netSentiment', 'countLast24Hours', 'netSentimentLast24Hours'];

    useSetExportConfig(instanceId, {
        title: title,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            headerNameOverrides: [
                'topic_name',
                'theme_names',
                'share_of_voice',
                'posts',
                'net_sentiment',
                'posts_last_24_hours',
                'net_sentiment_last_24_hours'
            ],
            metricGroups: [_.map(topicMetrics, (key) => metricRequests[key])]
        },
        pdfExportConfig: {
            exportOrientation: RivalIQ.enums.orientation.LANDSCAPE,
            exportWidth: 970
        }
    });

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title
        }
    });

    return (
        <Panel
            instanceId={instanceId}
            title={title}
            exportSubtitle={subtitle}
        >
            <QuidTopicsTable
                metricRequests={metricRequests}
                instanceId={instanceId}
            />
        </Panel>
    );
}
